import React, { useEffect, useRef, useState } from "react";

/** "Recycle or Trash?" — sort each item into the right bin. Teaches real recycling. */

type BinId = "recycle" | "compost" | "trash" | "ewaste";

const BINS: { id: BinId; label: string; emoji: string; cls: string }[] = [
  { id: "recycle", label: "Recycling", emoji: "♻️", cls: "blue" },
  { id: "compost", label: "Compost", emoji: "🌱", cls: "green" },
  { id: "trash", label: "Trash", emoji: "🗑️", cls: "gray" },
  { id: "ewaste", label: "E-Waste", emoji: "🔌", cls: "orange" },
];

type Item = { emoji: string; name: string; bin: BinId; fact: string };

const ITEMS: Item[] = [
  { emoji: "🍌", name: "Banana peel", bin: "compost", fact: "Food scraps become rich compost! 🌱" },
  { emoji: "🔋", name: "Battery", bin: "ewaste", fact: "Batteries are hazardous — never in the trash!" },
  { emoji: "📺", name: "Old TV", bin: "ewaste", fact: "Electronics need special recycling." },
  { emoji: "💻", name: "Laptop", bin: "ewaste", fact: "We refurbish working laptops for families!" },
  { emoji: "📱", name: "Phone", bin: "ewaste", fact: "Phones have valuable metals to recover." },
  { emoji: "🔌", name: "Charger & cables", bin: "ewaste", fact: "Cables count as e-waste too." },
  { emoji: "🧴", name: "Plastic bottle", bin: "recycle", fact: "Empty & rinse it first!" },
  { emoji: "🥫", name: "Metal can", bin: "recycle", fact: "Cans are recycled endlessly." },
  { emoji: "📰", name: "Newspaper", bin: "recycle", fact: "Clean paper is easy to recycle." },
  { emoji: "📦", name: "Cardboard box", bin: "recycle", fact: "Flatten it to save space!" },
  { emoji: "🍷", name: "Glass bottle", bin: "recycle", fact: "Glass recycles forever." },
  { emoji: "🧃", name: "Juice carton", bin: "recycle", fact: "Cartons go with recycling in NYC." },
  { emoji: "🍎", name: "Apple core", bin: "compost", fact: "Fruit scraps love the compost bin." },
  { emoji: "☕", name: "Coffee grounds", bin: "compost", fact: "Coffee grounds make great compost." },
  { emoji: "🍕", name: "Greasy pizza box", bin: "trash", fact: "Grease ruins recycling — sadly trash!" },
  { emoji: "🥤", name: "Styrofoam cup", bin: "trash", fact: "Styrofoam isn't recyclable in NYC." },
  { emoji: "🍬", name: "Candy wrapper", bin: "trash", fact: "Mixed-material wrappers = trash." },
  { emoji: "💡", name: "Lightbulb", bin: "ewaste", fact: "Bulbs go with e-waste, not glass." },
];

type Phase = "ready" | "playing" | "over";

const RecycleGame: React.FC = () => {
  const [phase, setPhase] = useState<Phase>("ready");
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [combo, setCombo] = useState(0);
  const [item, setItem] = useState<Item | null>(null);
  const [feedback, setFeedback] = useState<{ ok: boolean; text: string } | null>(null);
  const [timePct, setTimePct] = useState(100);
  const [best, setBest] = useState(0);

  const lastIdx = useRef(-1);
  const livesRef = useRef(3);
  const correctRef = useRef(0);
  const comboRef = useRef(0);

  const pickItem = () => {
    let idx = (Math.random() * ITEMS.length) | 0;
    if (idx === lastIdx.current) idx = (idx + 1) % ITEMS.length;
    lastIdx.current = idx;
    setItem(ITEMS[idx]);
    setTimePct(100);
  };

  const start = () => {
    setScore(0); setLives(3); livesRef.current = 3;
    setCombo(0); comboRef.current = 0;
    correctRef.current = 0;
    setFeedback(null); setPhase("playing"); pickItem();
  };

  const finish = (correct: boolean, it: Item, timeout: boolean) => {
    if (correct) {
      const pts = 10 + Math.min(comboRef.current, 8) * 2;
      comboRef.current += 1; correctRef.current += 1;
      setScore((s) => s + pts); setCombo(comboRef.current);
      setFeedback({ ok: true, text: "Nice! " + it.fact });
    } else {
      const binLabel = BINS.find((b) => b.id === it.bin)!.label;
      livesRef.current -= 1; setLives(livesRef.current);
      comboRef.current = 0; setCombo(0);
      setFeedback({ ok: false, text: `${timeout ? "Too slow! " : "Oops! "}${it.name} → ${binLabel}. ${it.fact}` });
    }
    setTimeout(() => {
      setFeedback(null);
      if (livesRef.current <= 0) { setPhase("over"); }
      else pickItem();
    }, 1250);
  };

  // per-item countdown
  useEffect(() => {
    if (phase !== "playing" || !item || feedback) return;
    const duration = Math.max(1700, 4400 - correctRef.current * 130);
    const started = Date.now();
    const id = setInterval(() => {
      const elapsed = Date.now() - started;
      setTimePct(Math.max(0, 100 - (elapsed / duration) * 100));
      if (elapsed >= duration) { clearInterval(id); finish(false, item, true); }
    }, 50);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, phase, feedback]);

  const answer = (id: BinId) => {
    if (feedback || !item || phase !== "playing") return;
    finish(id === item.bin, item, false);
  };

  // track best across games
  useEffect(() => { if (phase === "over") setBest((b) => Math.max(b, score)); }, [phase, score]);

  return (
    <div className="ee-rt">
      <div className="ee-rt-stage">
        {phase === "ready" && (
          <div className="ee-rt-overlay">
            <div className="ee-rt-emoji">♻️</div>
            <h3 className="tbg-title">RECYCLE<br />OR TRASH?</h3>
            <p className="tbg-sub">Sort each item into the right bin as fast as you can. Learn while you play! 🌎</p>
            <button className="tbg-btn" onClick={start}>▶ PLAY</button>
          </div>
        )}

        {phase === "playing" && item && (
          <div className="ee-rt-play">
            <div className="ee-rt-hud">
              <span className="ee-rt-score">SCORE {score}</span>
              <span className="ee-rt-combo">{combo > 1 ? `🔥 x${combo}` : ""}</span>
              <span className="ee-rt-lives">{"❤️".repeat(Math.max(0, lives))}</span>
            </div>

            <div className="ee-rt-itembox">
              <div className="ee-rt-timer"><span style={{ width: timePct + "%" }} /></div>
              <div className={`ee-rt-item${feedback ? (feedback.ok ? " good" : " bad") : ""}`}>{item.emoji}</div>
              <div className="ee-rt-itemname">{item.name}</div>
              {feedback && <div className={`ee-rt-fb ${feedback.ok ? "good" : "bad"}`}>{feedback.text}</div>}
            </div>

            <div className="ee-rt-bins">
              {BINS.map((b) => (
                <button key={b.id} className={`ee-rt-bin ${b.cls}`} onClick={() => answer(b.id)} disabled={!!feedback}>
                  <span className="ee-rt-bin-emoji">{b.emoji}</span>
                  {b.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {phase === "over" && (
          <div className="ee-rt-overlay ee-rt-over">
            <div className="ee-rt-emoji">🏆</div>
            <h3 className="tbg-title">GREAT SORTING!</h3>
            <p className="tbg-sub">Score: <strong>{score}</strong>{best > score ? ` · Best: ${best}` : ""}<br />You're a recycling pro! ♻️</p>
            <button className="tbg-btn" onClick={start}>↻ PLAY AGAIN</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecycleGame;
